import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isRTL } from '@/core/i18n'
import { Search, FileDown, Copy, Printer, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import {
  TableColumnVisibilityMenu,
  initialColumnVisibility,
  countVisibleInRange,
  appendSelectedColumnsParam,
} from '@/components/shared/TableColumnVisibilityMenu'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { branchService } from '@/services/branchService'
import { reportService, TrialBalanceEntry } from '@/services/reportService'
import { BranchEntity } from '@/api/types'
import { formatCurrency } from '@/lib/format'

const TRIAL_BALANCE_COLUMN_ORDER = [
  'code',
  'account',
  'type',
  'head',
  'subHead',
  'subSubHead',
  'oDebit',
  'oCredit',
  'oTotal',
  'aDebit',
  'aCredit',
  'aTotal',
  'cDebit',
  'cCredit',
  'cTotal',
] as const

export const TrialBalanceReport: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const rtl = isRTL(i18n.language)
  const { user } = useAuthStore()

  const [trialBalanceData, setTrialBalanceData] = useState<TrialBalanceEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [branches, setBranches] = useState<BranchEntity[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [colVis, setColVis] = useState<Record<string, boolean>>(() =>
    initialColumnVisibility([...TRIAL_BALANCE_COLUMN_ORDER]),
  )

  // Pagination states
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [ledgerTotals, setLedgerTotals] = useState({
    oDebit: 0,
    oCredit: 0,
    oTotal: 0,
    aDebit: 0,
    aCredit: 0,
    aTotal: 0,
    cDebit: 0,
    cCredit: 0,
    cTotal: 0,
  })

  // Filter states
  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')
  const [selectedBranchId, setSelectedBranchId] = useState<number | undefined>(undefined)
  const [accountFilter, setAccountFilter] = useState<'balances' | 'all'>('balances')

  // Load branches and set default branch
  useEffect(() => {
    const loadBranches = async () => {
      try {
        const branchList = await branchService.getAllBranches()
        setBranches(branchList || [])
        // Set default branch to user's selected branch if available and not already set
        if (user?.selectedBranch?.branchId && !selectedBranchId) {
          const branchExists = branchList?.some(b => b.id === user.selectedBranch?.branchId)
          if (branchExists) {
            setSelectedBranchId(user.selectedBranch.branchId)
          }
        }
      } catch (error: any) {
        // Error handled silently
      }
    }
    loadBranches()
  }, [])

  // Set default dates (current month)
  useEffect(() => {
    const today = new Date()
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    
    setFromDate(formatDateForInput(firstDay))
    setToDate(formatDateForInput(lastDay))
  }, [])

  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const formatAmount = (amount?: number | null) => {
    if (amount === undefined || amount === null || amount === 0) return '-'
    return formatCurrency(Number(amount))
  }

  const handleSearch = async (resetPage: boolean = false) => {
    if (!selectedBranchId) {
      toast.error('Please select a branch')
      return
    }

    if (!fromDate || !toDate) {
      toast.error('Please select both from date and to date')
      return
    }

    try {
      setLoading(true)
      // Convert dates to ISO date-time format (YYYY-MM-DDTHH:mm:ss)
      const fromDateTime = `${fromDate}T00:00:00`
      const toDateTime = `${toDate}T23:59:59`
      
      const currentPage = resetPage ? 1 : pageNumber
      if (resetPage) {
        setPageNumber(1)
      }
      
      const response = await reportService.getTrialBalanceReport({
        fromDate: fromDateTime,
        toDate: toDateTime,
        branchId: selectedBranchId,
        pageNumber: currentPage,
        pageSize: pageSize,
        withBalancesOnly: accountFilter === 'balances',
      })
      setTrialBalanceData(response.data || [])
      setTotalCount(response.totalRecords || 0)
      setLedgerTotals({
        oDebit: response.totalODebit ?? 0,
        oCredit: response.totalOCredit ?? 0,
        oTotal: response.totalOTotal ?? 0,
        aDebit: response.totalADebit ?? 0,
        aCredit: response.totalACredit ?? 0,
        aTotal: response.totalATotal ?? 0,
        cDebit: response.totalCDebit ?? response.totalDebit ?? 0,
        cCredit: response.totalCCredit ?? response.totalCredit ?? 0,
        cTotal: response.totalCTotal ?? response.overallBalance ?? 0,
      })
      const pages = response.totalRecords ? Math.ceil(response.totalRecords / pageSize) : 0
      setTotalPages(pages)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load trial balance report')
      setTrialBalanceData([])
      setTotalCount(0)
      setTotalPages(0)
      setLedgerTotals({
        oDebit: 0,
        oCredit: 0,
        oTotal: 0,
        aDebit: 0,
        aCredit: 0,
        aTotal: 0,
        cDebit: 0,
        cCredit: 0,
        cTotal: 0,
      })
    } finally {
      setLoading(false)
    }
  }


  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return trialBalanceData
    const query = searchQuery.toLowerCase()
    return trialBalanceData.filter((entry) => {
      return (
        (entry.code || '').toLowerCase().includes(query) ||
        (entry.account || '').toLowerCase().includes(query) ||
        (entry.type || '').toLowerCase().includes(query) ||
        (entry.head || '').toLowerCase().includes(query) ||
        (entry.subHead || '').toLowerCase().includes(query) ||
        (entry.subSubHead || '').toLowerCase().includes(query)
      )
    })
  }, [trialBalanceData, searchQuery])

  const tbColumnMenu = useMemo(
    () => [
      { id: 'code', label: t('reports.code') },
      { id: 'account', label: t('reports.accountTitle') },
      { id: 'type', label: t('reports.type') },
      { id: 'head', label: t('reports.head') },
      { id: 'subHead', label: t('reports.subHead') },
      { id: 'subSubHead', label: t('reports.subSubHead') },
      { id: 'oDebit', label: t('reports.openingDr') },
      { id: 'oCredit', label: t('reports.openingCr') },
      { id: 'oTotal', label: `${t('reports.openingBalance')} — ${t('reports.balance')}` },
      { id: 'aDebit', label: t('reports.debitDr') },
      { id: 'aCredit', label: t('reports.creditCr') },
      { id: 'aTotal', label: `${t('reports.transactions')} — ${t('reports.balance')}` },
      { id: 'cDebit', label: t('reports.totalDebitDr') },
      { id: 'cCredit', label: t('reports.totalCreditCr') },
      { id: 'cTotal', label: `${t('reports.balance')} (${t('reports.total')})` },
    ],
    [t],
  )

  const toggleTbColumn = (id: string) => {
    setColVis((p) => ({ ...p, [id]: !p[id] }))
  }

  const accGroupSpan = countVisibleInRange(colVis, TRIAL_BALANCE_COLUMN_ORDER, 0, 6)
  const openGroupSpan = countVisibleInRange(colVis, TRIAL_BALANCE_COLUMN_ORDER, 6, 9)
  const txnGroupSpan = countVisibleInRange(colVis, TRIAL_BALANCE_COLUMN_ORDER, 9, 12)
  const balGroupSpan = countVisibleInRange(colVis, TRIAL_BALANCE_COLUMN_ORDER, 12, 15)

  const branchOptions: SearchableSelectOption[] = branches.map((branch) => {
    const branchId = branch.id || (branch as any).branchId || 0
    return {
      value: String(branchId),
      label: branch.nameEn || branch.nameAr || `Branch ${branchId}`,
    }
  })

  const handleBranchChange = (value: string) => {
    setSelectedBranchId(value ? Number(value) : undefined)
  }

  // Handle page size change
  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setPageNumber(1)
    // Trigger search with new page size
    if (selectedBranchId && fromDate && toDate) {
      setTimeout(() => handleSearch(true), 0)
    }
  }

  // Handle page change
  useEffect(() => {
    if (selectedBranchId && fromDate && toDate && pageNumber > 1 && trialBalanceData.length > 0) {
      handleSearch(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber])

  const handleCopy = () => {
    toast.success('Copy functionality coming soon')
  }

  const handleCsv = () => {
    if (filteredData.length === 0) {
      toast.error('No data to export')
      return
    }

    // Create CSV content
    const headers = [
      'Code',
      'Account Title',
      'Type',
      'Head',
      'Sub Head',
      'Sub Sub Head',
      'Opening DR',
      'Opening CR',
      'Opening Balance',
      'Debit DR',
      'Credit CR',
      'Transaction Balance',
      'Total Debit DR',
      'Total Credit CR',
      'Total Balance',
    ].join(',')

    const rows = filteredData.map(entry => [
      `"${entry.code || ''}"`,
      `"${entry.account || ''}"`,
      `"${entry.type || ''}"`,
      `"${entry.head || ''}"`,
      `"${entry.subHead || ''}"`,
      `"${entry.subSubHead || ''}"`,
      `"${formatAmount(entry.oDebit)}"`,
      `"${formatAmount(entry.oCredit)}"`,
      `"${formatAmount(entry.oTotal)}"`,
      `"${formatAmount(entry.aDebit)}"`,
      `"${formatAmount(entry.aCredit)}"`,
      `"${formatAmount(entry.aTotal)}"`,
      `"${formatAmount(entry.cDebit)}"`,
      `"${formatAmount(entry.cCredit)}"`,
      `"${formatAmount(entry.cTotal)}"`,
    ].join(','))

    // Add total row
    const totalRow = [
      '"Total"',
      '""',
      '""',
      '""',
      '""',
      '""',
      `"${formatAmount(ledgerTotals.oDebit)}"`,
      `"${formatAmount(ledgerTotals.oCredit)}"`,
      `"${formatAmount(ledgerTotals.oTotal)}"`,
      `"${formatAmount(ledgerTotals.aDebit)}"`,
      `"${formatAmount(ledgerTotals.aCredit)}"`,
      `"${formatAmount(ledgerTotals.aTotal)}"`,
      `"${formatAmount(ledgerTotals.cDebit)}"`,
      `"${formatAmount(ledgerTotals.cCredit)}"`,
      `"${formatAmount(ledgerTotals.cTotal)}"`,
    ].join(',')

    const csvContent = [headers, ...rows, totalRow].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    const dateStr = fromDate && toDate ? `${fromDate}_${toDate}` : 'report'
    link.setAttribute('download', `trial-balance-${dateStr}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('CSV file downloaded')
  }

  const handlePrint = () => {
    if (!selectedBranchId || !fromDate || !toDate) {
      toast.error('Please select branch and date range before printing')
      return
    }
    
    // Navigate to print page with query parameters
    const params = new URLSearchParams({
      fromDate,
      toDate,
      branchId: String(selectedBranchId),
      withBalancesOnly: accountFilter === 'balances' ? 'true' : 'false',
    })
    appendSelectedColumnsParam(params, colVis)
    
    window.open(`/reports/trial-balance/print?${params.toString()}`, '_blank')
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.trialBalanceReport')}
        description={t('reports.viewTrialBalance')}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>{t('reports.fromDate')}</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.toDate')}</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.branch')}</Label>
              <SearchableSelect
                options={branchOptions}
                value={selectedBranchId ? String(selectedBranchId) : ''}
                onValueChange={handleBranchChange}
                placeholder={t('reports.selectBranch')}
                searchPlaceholder={t('reports.searchBranches')}
                emptyMessage={t('reports.noBranchesFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.accountFilter')}</Label>
              <Select
                value={accountFilter}
                onValueChange={(value) => setAccountFilter(value as 'balances' | 'all')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="balances">{t('reports.accountsWithBalances')}</SelectItem>
                  <SelectItem value="all">{t('reports.allAccounts')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <Label>{t('reports.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('reports.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => handleSearch(true)} disabled={loading} className="bg-black text-white hover:bg-gray-800">
                {loading ? t('reports.loading') : t('reports.search')}
              </Button>
              <TableColumnVisibilityMenu columns={tbColumnMenu} visibility={colVis} onToggle={toggleTbColumn} />
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-2" />
                {t('reports.copy')}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handlePrint}
                className="bg-black text-white hover:bg-gray-800"
              >
                <Printer className="h-4 w-4 mr-2" />
                {t('reports.print')}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleCsv}
              >
                <FileDown className="h-4 w-4 mr-2" />
                CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading report...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title="No data found"
                description="Click Search to load trial balance report"
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 bg-primary/5">
                      {accGroupSpan > 0 && (
                        <TableHead
                          className="border-r-2 border-gray-300 text-center font-bold text-primary"
                          colSpan={accGroupSpan}
                        >
                          {t('reports.account')}
                        </TableHead>
                      )}
                      {openGroupSpan > 0 && (
                        <TableHead
                          className="border-r-2 border-gray-300 text-center font-bold text-primary"
                          colSpan={openGroupSpan}
                        >
                          {t('reports.openingBalance')}
                        </TableHead>
                      )}
                      {txnGroupSpan > 0 && (
                        <TableHead
                          className="border-r-2 border-gray-300 text-center font-bold text-primary"
                          colSpan={txnGroupSpan}
                        >
                          {t('reports.transactions')}
                        </TableHead>
                      )}
                      {balGroupSpan > 0 && (
                        <TableHead className="text-center font-bold text-primary" colSpan={balGroupSpan}>
                          {t('reports.balance')}
                        </TableHead>
                      )}
                    </TableRow>
                    <TableRow className="border-b-2 border-primary/20 bg-primary/5">
                      {colVis.code && (
                        <TableHead className={`font-bold text-primary ${rtl ? 'pr-6 text-right' : 'pl-6 text-left'}`}>
                          {t('reports.code')}
                        </TableHead>
                      )}
                      {colVis.account && (
                        <TableHead className={`font-bold text-primary ${rtl ? 'pr-6 text-right' : 'pl-6 text-left'}`}>
                          {t('reports.accountTitle')}
                        </TableHead>
                      )}
                      {colVis.type && (
                        <TableHead className={`font-bold text-primary ${rtl ? 'pr-6 text-right' : 'pl-6 text-left'}`}>
                          {t('reports.type')}
                        </TableHead>
                      )}
                      {colVis.head && (
                        <TableHead className={`font-bold text-primary ${rtl ? 'pr-6 text-right' : 'pl-6 text-left'}`}>
                          {t('reports.head')}
                        </TableHead>
                      )}
                      {colVis.subHead && (
                        <TableHead className={`font-bold text-primary ${rtl ? 'pr-6 text-right' : 'pl-6 text-left'}`}>
                          {t('reports.subHead')}
                        </TableHead>
                      )}
                      {colVis.subSubHead && (
                        <TableHead
                          className={`border-r-2 border-gray-300 font-bold text-primary ${rtl ? 'pr-6 text-right' : 'pl-6 text-left'}`}
                        >
                          {t('reports.subSubHead')}
                        </TableHead>
                      )}
                      {colVis.oDebit && (
                        <TableHead className="text-right font-bold text-primary">{t('reports.openingDr')}</TableHead>
                      )}
                      {colVis.oCredit && (
                        <TableHead className="text-right font-bold text-primary">{t('reports.openingCr')}</TableHead>
                      )}
                      {colVis.oTotal && (
                        <TableHead className="border-r-2 border-gray-300 text-right font-bold text-primary">
                          {t('reports.balance')}
                        </TableHead>
                      )}
                      {colVis.aDebit && (
                        <TableHead className="text-right font-bold text-primary">{t('reports.debitDr')}</TableHead>
                      )}
                      {colVis.aCredit && (
                        <TableHead className="text-right font-bold text-primary">{t('reports.creditCr')}</TableHead>
                      )}
                      {colVis.aTotal && (
                        <TableHead className="border-r-2 border-gray-300 text-right font-bold text-primary">
                          {t('reports.balance')}
                        </TableHead>
                      )}
                      {colVis.cDebit && (
                        <TableHead className="text-right font-bold text-primary">{t('reports.totalDebitDr')}</TableHead>
                      )}
                      {colVis.cCredit && (
                        <TableHead className="text-right font-bold text-primary">{t('reports.totalCreditCr')}</TableHead>
                      )}
                      {colVis.cTotal && (
                        <TableHead className="text-right font-bold text-primary">{t('reports.balance')}</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((entry, index) => (
                      <TableRow
                        key={index}
                        className="border-b hover:bg-primary/5 transition-colors"
                      >
                        {colVis.code && <TableCell className="px-3 py-3">{entry.code || '-'}</TableCell>}
                        {colVis.account && <TableCell className="px-3 py-3">{entry.account || '-'}</TableCell>}
                        {colVis.type && <TableCell className="px-3 py-3">{entry.type || '-'}</TableCell>}
                        {colVis.head && <TableCell className="px-3 py-3">{entry.head || '-'}</TableCell>}
                        {colVis.subHead && <TableCell className="px-3 py-3">{entry.subHead || '-'}</TableCell>}
                        {colVis.subSubHead && (
                          <TableCell className="border-r-2 border-gray-300 px-3 py-3">{entry.subSubHead || '-'}</TableCell>
                        )}
                        {colVis.oDebit && (
                          <TableCell className="px-3 py-3 text-right">{formatAmount(entry.oDebit)}</TableCell>
                        )}
                        {colVis.oCredit && (
                          <TableCell className="px-3 py-3 text-right">{formatAmount(entry.oCredit)}</TableCell>
                        )}
                        {colVis.oTotal && (
                          <TableCell className="border-r-2 border-gray-300 px-3 py-3 text-right">
                            {formatAmount(entry.oTotal)}
                          </TableCell>
                        )}
                        {colVis.aDebit && (
                          <TableCell className="px-3 py-3 text-right">{formatAmount(entry.aDebit)}</TableCell>
                        )}
                        {colVis.aCredit && (
                          <TableCell className="px-3 py-3 text-right">{formatAmount(entry.aCredit)}</TableCell>
                        )}
                        {colVis.aTotal && (
                          <TableCell className="border-r-2 border-gray-300 px-3 py-3 text-right">
                            {formatAmount(entry.aTotal)}
                          </TableCell>
                        )}
                        {colVis.cDebit && (
                          <TableCell className="px-3 py-3 text-right">{formatAmount(entry.cDebit)}</TableCell>
                        )}
                        {colVis.cCredit && (
                          <TableCell className="px-3 py-3 text-right">{formatAmount(entry.cCredit)}</TableCell>
                        )}
                        {colVis.cTotal && (
                          <TableCell className="px-3 py-3 text-right">{formatAmount(entry.cTotal)}</TableCell>
                        )}
                      </TableRow>
                    ))}
                    {/* Total Row with Yellow Background */}
                    <TableRow className="border-t-2 border-primary/20 bg-yellow-200 font-bold dark:bg-yellow-900/30">
                      <TableCell
                        colSpan={Math.max(1, countVisibleInRange(colVis, TRIAL_BALANCE_COLUMN_ORDER, 0, 6))}
                        className="px-3 py-3 text-right font-bold"
                      >
                        Total
                      </TableCell>
                      {colVis.oDebit && (
                        <TableCell className="px-3 py-3 text-right">{formatAmount(ledgerTotals.oDebit)}</TableCell>
                      )}
                      {colVis.oCredit && (
                        <TableCell className="px-3 py-3 text-right">{formatAmount(ledgerTotals.oCredit)}</TableCell>
                      )}
                      {colVis.oTotal && (
                        <TableCell className="border-r-2 border-gray-300 px-3 py-3 text-right">
                          {formatAmount(ledgerTotals.oTotal)}
                        </TableCell>
                      )}
                      {colVis.aDebit && (
                        <TableCell className="px-3 py-3 text-right">{formatAmount(ledgerTotals.aDebit)}</TableCell>
                      )}
                      {colVis.aCredit && (
                        <TableCell className="px-3 py-3 text-right">{formatAmount(ledgerTotals.aCredit)}</TableCell>
                      )}
                      {colVis.aTotal && (
                        <TableCell className="border-r-2 border-gray-300 px-3 py-3 text-right">
                          {formatAmount(ledgerTotals.aTotal)}
                        </TableCell>
                      )}
                      {colVis.cDebit && (
                        <TableCell className="px-3 py-3 text-right">{formatAmount(ledgerTotals.cDebit)}</TableCell>
                      )}
                      {colVis.cCredit && (
                        <TableCell className="px-3 py-3 text-right">{formatAmount(ledgerTotals.cCredit)}</TableCell>
                      )}
                      {colVis.cTotal && (
                        <TableCell className="px-3 py-3 text-right">{formatAmount(ledgerTotals.cTotal)}</TableCell>
                      )}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  Showing {(pageNumber - 1) * pageSize + 1} to{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} entries
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Previous</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap px-2">
                    Page {pageNumber} of {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.min(prev + 1, totalPages || 1))}
                    disabled={pageNumber >= totalPages || loading}
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
